import { useCallback, useState, type FC } from 'react';
import { useHistory } from 'react-router-dom';
import { Table } from 'components';
import { RoleName, type SerializedMeterModel } from '@enmon/commons/lib/tools';
import type { SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { useDeleteMeterModel } from 'api/hooks/meter-models/useDeleteMeterModel';
import { useUserInfo } from '@/hooks/useUser';
import type { Key, SortOrder } from 'antd/lib/table/interface';
import { getModelsColumnsConfig } from '../utils/getColumnsConfig';
import { useModelsListFilterWithPagination } from '../hooks/useModelsListFilterWithPagination';
import type { SerializedMeterModelWithCount } from '@/lib/services';

interface ModelsTableProps {
  isFetchingModels: boolean;
  modelsData: SerializedMeterModelWithCount[];
}

export const ModelsTable: FC<ModelsTableProps> = ({ isFetchingModels, modelsData }) => {
  // State
  const history = useHistory();
  const { role } = useUserInfo() ?? {};
  const { pagination, setFilterAndPaginationState } = useModelsListFilterWithPagination();
  const isMagicRole = role?.name === RoleName.Magic;
  const [sortState, setSortState] = useState<{ field: Key | null; order: SortOrder }>({
    field: null,
    order: null,
  });

  // Mutations
  const { mutate: deleteModel, isLoading: isDeletingModels } = useDeleteMeterModel();

  // Handlers
  const handleClick = useCallback(
    (modelId: SerializedMeterModel['id']) => {
      history.push(`${history.location.pathname}/${modelId}`);
    },
    [history],
  );

  const handleDelete = useCallback(
    (id: SerializedMeterModel['id']) => {
      deleteModel(id);
    },
    [deleteModel],
  );

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<SerializedMeterModelWithCount> | SorterResult<SerializedMeterModelWithCount>[],
  ) => {
    const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) ?? {};
    const newFilter = {
      ...(columnKey &&
        order && {
          sort: { [columnKey.toString()]: `${order.slice(0, -3)}` },
        }),
    };
    setSortState({ field: columnKey, order });
    setFilterAndPaginationState(newFilter, newPagination);
  };

  // Table Config
  const isLoading = isFetchingModels || isDeletingModels;

  const tableColumns = getModelsColumnsConfig({
    loading: isLoading,
    isMagicRole,
    onClick: handleClick,
    onDelete: handleDelete,
    sortState,
  });

  return (
    <Table<SerializedMeterModelWithCount>
      columns={tableColumns}
      dataSource={modelsData}
      loading={isLoading}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} models`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={handleTableChange}
    />
  );
};
